import { useContext, useEffect, useState } from "react";
import { LearningTargetCard } from "./LearningTargetCard";
import { CourseStructureCtx, LockStatusMap, UserCtx } from "../../../interfaces";
import { QuestionType, _AuthType } from "../../../enums";
import { UserContext } from "../../../context/UserContext";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import {
  Target as TargetTree,
  Question as QuestionTree,
  TargetSkill as TargetSkillTree,
  Question,
} from "../../utils/CourseTree";
import { Divider, Link, Stack } from "@mui/material";
import { CourseLanguageOverrides } from "@backend-types";
import { BioSpineLessonCard } from "./BioSpineLessonCard";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";

interface ModuleTargetListProps {
  targets: TargetTree[];
  targetLockMap?: LockStatusMap;
  openSkillVideoModal: (skills: TargetSkillTree[] | undefined) => void;
  languageOverrides?: CourseLanguageOverrides;
  groupTargets?: boolean;
}

export const ModuleTargetList = ({
  targets,
  targetLockMap,
  openSkillVideoModal,
  languageOverrides,
  groupTargets = false, // default set to false
}: ModuleTargetListProps) => {
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const [moduleId, setModuleId] = useState("");
  const [userTargetResumeMap, setUserTargetResumeMap] = useState<Map<string, Question>>();

  useEffect(() => {
    if (!courseStructureCtx.currentModuleId) return;
    setModuleId(courseStructureCtx.currentModuleId);
  }, [courseStructureCtx.currentModuleId]);

  // update target mastery, resume, and skills maps on module/course structure change
  useEffect(() => {
    const userTargetResumeMap = new Map();
    targets.forEach((target) => {
      // fall back to mastery problem if no problem to resume on
      // or go to mastery if its incorrect
      // AKA all problems complete
      // const masteryProblem = courseStructureCtx.targetMap[target.id].questions.find(
      //   (question: Question) => {
      //     return question.type === QuestionType.mastery;
      //   }
      // );
      const masteryProblem = target
        .getTargetQuestions()
        .find((question: QuestionTree) => {
          return question.type === QuestionType.mastery;
        });

      const masteryProblemIncorrect =
        masteryProblem &&
        masteryProblem.is_question_submitted &&
        !userCtx.checkPerfectScore(masteryProblem.id);

      // construct map entry
      // fall back to mastery problem if there's nothing to resume
      // if there's a mastery submission and its incorrect, resume there
      userTargetResumeMap.set(
        target.id,
        masteryProblemIncorrect
          ? masteryProblem
          : userCtx.getProblemToResumeOnTarget(target.id) ?? masteryProblem
      );
      // construct skill map entry
      //userTargetSkillsMap.set(target.id, courseStructureCtx.targetMap[target.id].skills);
    });
    setUserTargetResumeMap(userTargetResumeMap);
  }, [userCtx.getProblemToResumeOnTarget, targets, userCtx, moduleId]);

  return (
    <>
      {targets &&
        targets.map((target, index) => {
          const questions = target.getTargetQuestions() as Question[];
          const activityTypes = questions.map((item) => item.activity_type);
          const targetQuizScores = userCtx.userQuizSubmissions.filter(
            (item) => item.target_id === target.id
          );

          if (activityTypes.includes("orchard_node")) {
            return (
              <Stack key={target.id} sx={{ marginX: groupTargets ? "1.5rem" : 0 }}>
                <BioSpineLessonCard
                  languageOverrides={languageOverrides}
                  targetQuizSubmissions={targetQuizScores}
                  status={target.getTargetCompletionStatus()}
                  target={target}
                  lockStatus={
                    target.is_asset_locked
                      ? {
                          is_locked: true,
                          label: "Locked",
                          tooltip: (
                            <span>
                              Head back to{" "}
                              {MacsAuthenticator.authHook.authType === _AuthType.LTI &&
                              MacsAuthenticator.authHook.ltiReturnUrl ? (
                                <Link
                                  href={MacsAuthenticator.authHook.ltiReturnUrl}
                                  underline="always"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: "white",
                                    textDecorationColor: "white",
                                    "&:hover": {
                                      color: "#0092E0",
                                      textDecorationColor: "#0092E0",
                                    },
                                  }}
                                  onClick={(e) => e.stopPropagation()}>
                                  Canvas
                                </Link>
                              ) : (
                                "Canvas"
                              )}{" "}
                              to launch this submodule.
                            </span>
                          ),
                        }
                      : targetLockMap?.get(target.id)
                  }
                  key={target.id}
                  last={targets.length - 1 === index}
                  resumeQuestion={userTargetResumeMap?.get(target.id)}
                  openSkillVideoModal={() =>
                    openSkillVideoModal(
                      courseStructureCtx.courseTreeStructure?.getTargetSkills(target.id)
                    )
                  }
                  groupTargets={groupTargets}
                />
                {groupTargets && index !== targets.length - 1 && (
                  <Divider sx={{ borderWidth: "1px" }} />
                )}
              </Stack>
            );
          } else {
            return (
              <LearningTargetCard
                languageOverrides={languageOverrides}
                status={target.getTargetCompletionStatus()}
                target={target}
                lockStatus={targetLockMap?.get(target.id)}
                key={target.id}
                last={targets.length - 1 === index}
                resumeQuestion={userTargetResumeMap?.get(target.id)}
                openSkillVideoModal={() =>
                  openSkillVideoModal(
                    courseStructureCtx.courseTreeStructure?.getTargetSkills(target.id)
                  )
                }
              />
            );
          }
        })}
    </>
  );
};
